import React, {useCallback, useState} from 'react';

import {
    sendEventRemoveCart,
    sendEventOnRemoveSuccess,
    sendEventOnRemoveCancel,
} from '@optimaxdev/analytics/desktop/lib/cart/commonCartEvents';
import {Tooltip} from '@optimaxdev/design-system';
import classnames from 'classnames';
import {connect} from 'react-redux';

import {ApplicationStoreType} from 'desktop/store';

import {spawnPriorityTask} from 'libs/timers/spawnPriorityTask/spawnPriorityTask';

import {removeQuoteItem} from '../../store/reducers/quoteItems/quoteItems';
import {getIsCartLoaded, getIsRelatedToAccessory} from '../../store/selectors';
import {CartItemType} from '../../types';
import {BaseRemoveTooltip} from '../baseRemoveTooltip/baseRemoveTooltip';

import s from './baseRemoveButton.scss';

export type PropsType = {
    className?: string;
    item: CartItemType;
    removeItem: (data: {item: CartItemType; isNotice?: boolean}) => void;
    isRelatedToAccessory: boolean;
    isReadyForRemove: boolean;
    children?: React.ReactNode;
};

/**
 * Remove button for cart item
 * @param {PropsType} props - component props
 * @returns {React.ReactElement} remove button component
 */
export const BaseRemoveButton: React.FC<PropsType> = ({
    children = 'Remove',
    className,
    isReadyForRemove,
    isRelatedToAccessory = false,
    item,
    removeItem,
}) => {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    const onRemoveClick = useCallback(() => {
        setIsTooltipOpen(true);
        spawnPriorityTask(sendEventRemoveCart);
    }, []);

    const onRemoveSuccess = useCallback(() => {
        setIsTooltipOpen(false);
        removeItem({item});
        spawnPriorityTask(sendEventOnRemoveSuccess);
    }, [removeItem, item]);

    const onRemoveCancel = useCallback(() => {
        setIsTooltipOpen(false);
        spawnPriorityTask(sendEventOnRemoveCancel);
    }, []);

    return (
        <Tooltip
            content={
                <BaseRemoveTooltip
                    isRelatedToAccessory={isRelatedToAccessory}
                    itemName={item.window_name}
                    onCancel={onRemoveCancel}
                    onSuccess={onRemoveSuccess}
                />
            }
            closeTrigger="click"
            containerClassName={s.baseRemoveButtonTooltip}
            isOpen={isTooltipOpen}
            offset={-50}
            openTrigger="click"
            position="right-start"
            renderAs="div"
        >
            <div className={classnames(className, s.removeButton)}>
                <button
                    className={classnames(s.button, {
                        [s.removeButtonCartInAccessory]: item.type === 'accessories',
                    })}
                    data-test-name="removeCartItem"
                    disabled={!isReadyForRemove}
                    onClick={onRemoveClick}
                    type="button"
                >
                    {children}
                </button>
            </div>
        </Tooltip>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @param {PropsType} props - component own props
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType, props: {item: {id: string}}) => ({
    isReadyForRemove: getIsCartLoaded(store),
    isRelatedToAccessory: getIsRelatedToAccessory(store, props.item.id),
});

export const BaseRemoveButtonConnected = connect(mapStateToProps, {removeItem: removeQuoteItem})(
    BaseRemoveButton,
);
